import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectModule } from '@openng/optimus-ui/select';
import { Subject, takeUntil } from 'rxjs';

import { BaseComponent } from '../../../common/BaseComponent';
import { GlobalTemplateService } from '../../../../../services/global-template.service';
import { ToastBarService } from '../../../../../services/toast-bar.service';
import { VariableMaxLengthService } from '../../../../../services/variable-max-length.service';
import {
  GenSingleImageLogoInitModel,
  GenSingleImageLogoInitVariables,
  GenSingleImageLogoModel,
  GenSingleImageLogoVariables,
} from '../../../../../models/google-template/gen-single-image-logo.interface';
import { BrandLogo, BrandLogoHelper } from '../../../../../utils/brand-logo-helper';
import { Helper } from '../../../../../utils/helper';

/**
 * Picks which brand logo the Demand Gen previews put on the ad. Only the
 * subdirectory name is stored, so the asset can be re-exported or moved without
 * touching saved templates.
 */
@Component({
  selector: 'app-gen-single-image-logo',
  standalone: true,
  imports: [CommonModule, FormsModule, SelectModule],
  templateUrl: './gen-single-image-logo.component.html',
  styleUrl: './gen-single-image-logo.component.scss',
})
export class GenSingleImageLogoComponent extends BaseComponent implements OnInit, OnDestroy {
  //@ input
  @Input() block: GenSingleImageLogoModel = new GenSingleImageLogoInitModel();
  @Input() isPreview = false;
  //@ output
  @Output() blockData = new EventEmitter<any>(); // Event emitter to send data to the parent

  // Copied, not aliased: p-dropdown's [options] is typed as a mutable any[],
  // which a readonly array cannot satisfy under strictTemplates.
  readonly logos: BrandLogo[] = [...BrandLogoHelper.LOGOS];
  selectedLogo: string = '';
  disabled: boolean = true;
  private _destroy$ = new Subject<void>();

  constructor(
    toastBarService: ToastBarService,
    private globalTemplateService: GlobalTemplateService,
    private _variableMaxLengthService: VariableMaxLengthService
  ) {
    super(toastBarService);
  }

  ngOnInit() {
    if (Helper.isEmpty(this.block.variables!)) {
      this.block.variables = new GenSingleImageLogoInitVariables();
    }

    // Preselect the saved brand (stored as its subdirectory name) when the editor opens.
    this.selectedLogo = this.block.variables!['Logo'] ?? '';

    // Disable the dropdown when the template is read-only or locked by another user.
    this.globalTemplateService
      .getIsDisabled()
      .pipe(takeUntil(this._destroy$))
      .subscribe((isDisabled: boolean) => {
        this.disabled = isDisabled;
      });

    // Register the Logo max length so the docx export prints a value (not "Undefined").
    this._variableMaxLengthService.addSingleVariableMaximumLength({
      name: 'Logo',
      maxLength: 4000,
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  //@ placeholders
  logoPlaceholder: string = 'Select brand logo';

  /**
   * The full option behind `selectedLogo`. The dropdown binds `optionValue`, so
   * ngModel only holds the folder name and the selected-item template has no
   * thumbnail to show without this lookup.
   */
  get selectedLogoOption(): BrandLogo | undefined {
    return this.logos.find((logo) => logo.folder === this.selectedLogo);
  }

  //@onChange
  onChange(value: any, fieldName: keyof GenSingleImageLogoVariables) {
    if (this.block.variables) {
      this.block.variables[fieldName] = value;
      this.blockData.emit(this.block);
    }
  }
}
